// In loving memory of carlosPato.js 03/03/2022
import { get, merge, mergeWith, assign, isArray } from 'lodash'
import { customConfig } from './default/customConfig';
import { tableColumns } from './default/tableColumns';
import { getPasswordRequirements, getPasswordSubmitValidators } from './helpers/passwordPolicies';
const config = {
  customConfig: merge({}, customConfig),
  tableColumns: assign({}, tableColumns)
}

export const getConfig = (path, defaultValue) => (get(config, path, defaultValue));

/**
 * Resolve a named password policy (declarative selector from the BFF) into the
 * function-shaped keys the create/change/reset-password screens consume.
 *
 * The selector (`passwordPolicy.variant` + `.minLength`, `passwordSubmitPolicy`)
 * travels in JSON, but `createOrChangePasswordReqsField`/`ReqsSubmit` are
 * functions and can't — so they live as named policies in passwordPolicies.js
 * and must be wired here. Used from BOTH paths:
 *  - post-login via applyBootstrapConfig (bootstrap), and
 *  - pre-login via useVisualConfig (visual-config) — reset/create-password are
 *    token-based flows with no JWT, so they never hit bootstrap.
 * Without this, every tenant falls back to the hardcoded default policy.
 */
export const applyPasswordPolicy = (passwordPolicy, passwordSubmitPolicy) => {
  if (!passwordPolicy || !passwordPolicy.variant) return;
  config.customConfig.createOrChangePasswordMinLengthValue =
    passwordPolicy.minLength ?? config.customConfig.createOrChangePasswordMinLengthValue;
  config.customConfig.createOrChangePasswordReqsField = (minLength) =>
    getPasswordRequirements(passwordPolicy.variant, minLength);
  config.customConfig.createOrChangePasswordReqsSubmit =
    getPasswordSubmitValidators(passwordSubmitPolicy);
};

/**
 * Apply the tenant's reCAPTCHA on/off into customConfig. The Login gate reads
 * `customConfig.googleCaptcha`, but the login screen is PRE-LOGIN, so it can't
 * wait for the bootstrap (post-login). This is fed pre-login from visual-config
 * via useVisualConfig — same rationale as applyPasswordPolicy.
 */
export const applyGoogleCaptcha = (googleCaptcha) => {
  if (googleCaptcha !== undefined) config.customConfig.googleCaptcha = googleCaptcha;
};

/**
 * Merge runtime bootstrap config (from BFF) into the config object.
 * Called once after the bootstrap response arrives post-login.
 */
export const applyBootstrapConfig = (bootstrapConfig) => {
  if (!bootstrapConfig) return;

  const { tableColumns: bsTableColumns, ...bsCustomConfig } = bootstrapConfig;

  // Arrays from the BFF (e.g. receipt summary, qr, contactData) must REPLACE
  // the defaults, not merge by index — BFF items use fieldPath (declarative)
  // while defaults use method (functions), and the lengths may differ.
  mergeWith(config.customConfig, bsCustomConfig, (objValue, srcValue) => {
    if (isArray(srcValue)) return srcValue;
  });

  applyPasswordPolicy(bsCustomConfig.passwordPolicy, bsCustomConfig.passwordSubmitPolicy);

  if (bsTableColumns) {
    assign(config.tableColumns, bsTableColumns);
  }
};